import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type UserRole = 'ARTISTA' | 'AUTOR' | 'EDITORA' | 'ADMIN';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isLoading, needsProfileCompletion } = useAuth();
  const location = useLocation();
  const [isChecking2FA, setIsChecking2FA] = useState(true); // Start as true for admins
  const [has2FA, setHas2FA] = useState<boolean | null>(null);

  // Check 2FA for admin users
  useEffect(() => {
    const check2FA = async () => {
      if (user?.role === "ADMIN" && location.pathname !== "/admin/setup-2fa") {
        setIsChecking2FA(true);
        try {
          const { data: factors } = await supabase.auth.mfa.listFactors();
          const hasVerifiedTOTP = factors?.totp?.some(f => f.status === "verified");
          setHas2FA(hasVerifiedTOTP ?? false);
        } catch (err) {
          console.error("Error checking 2FA:", err);
          setHas2FA(false);
        } finally {
          setIsChecking2FA(false);
        }
      } else {
        // Not an admin or on 2FA setup page, no need to check
        setIsChecking2FA(false);
      }
    };

    if (user) {
      check2FA();
    } else {
      setIsChecking2FA(false);
    }
  }, [user, location.pathname]);

  if (isLoading || (user?.role === "ADMIN" && isChecking2FA)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // Redirect to auth with current path as redirect parameter
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?redirect=${redirectPath}`} replace />;
  }

  // Check if user needs to complete profile (Google OAuth users)
  if (needsProfileCompletion && location.pathname !== "/completar-perfil") {
    return <Navigate to="/completar-perfil" replace />;
  }

  // Check role if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/acesso-negado" replace />;
  }

  // If admin without 2FA and not on setup page, redirect to setup
  if (user.role === "ADMIN" && has2FA === false && location.pathname !== "/admin/setup-2fa") {
    return <Navigate to="/admin/setup-2fa" replace />;
  }

  return <>{children}</>;
}
